import React, { useEffect, useState } from 'react';
import {
  LessonContentRenderer,
  LessonMediaBlock,
  renderFormattedText,
} from '../Features/ExecutiveTraining';
import { LessonMediaItem } from '../../services/trainingService';

interface PreviewLesson {
  title: string;
  duration: string;
  summary: string;
  contentMarkdown: string;
  takeawaysText: string;
  media: LessonMediaItem[];
}

interface PreviewQuestion {
  question: string;
  optionsText: string;
}

/** The subset of the editor draft that the learner-side preview needs. */
export interface AcademyPreviewModule {
  title: string;
  category: string;
  duration: string;
  isoClause: string;
  description: string;
  lessons: PreviewLesson[];
  quiz: PreviewQuestion[];
}

interface AcademyModulePreviewProps {
  module: AcademyPreviewModule;
  status: string;
  onClose: () => void;
}

const lines = (value: string): string[] =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const AcademyModulePreview: React.FC<AcademyModulePreviewProps> = ({
  module,
  status,
  onClose,
}) => {
  const [activeLessonIndex, setActiveLessonIndex] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const lessons = module.lessons;
  const activeLesson = lessons[activeLessonIndex];
  const lessonCount = lessons.length;

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Academy module draft preview"
    >
      <div className="mx-auto flex min-h-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-purple-800">
                Draft preview
              </span>
              <span className="text-[11px] text-slate-500">
                Status: {status} · reflects the current editor state — nothing is saved or live from
                here
              </span>
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900">
              {module.title || 'Untitled module'}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {[module.category, module.isoClause, module.duration].filter(Boolean).join(' • ')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="self-start rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 sm:self-center"
          >
            Close preview
          </button>
        </div>

        <div className="flex-1 space-y-4 p-4 sm:p-6">
          <div className="flex items-center space-x-2 overflow-x-auto pb-1">
            {lessons.map((lesson, index) => (
              <button
                key={`${lesson.title}-${index}`}
                type="button"
                onClick={() => {
                  setActiveLessonIndex(index);
                  setShowQuiz(false);
                }}
                className={`flex-shrink-0 rounded-lg px-3.5 py-2 text-xs font-medium transition-all ${
                  !showQuiz && activeLessonIndex === index
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Lesson {index + 1}:{' '}
                {lesson.title.length > 25
                  ? `${lesson.title.substring(0, 22)}...`
                  : lesson.title || 'Untitled'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowQuiz(true)}
              disabled={module.quiz.length === 0}
              className={`flex flex-shrink-0 items-center space-x-1 rounded-lg px-3.5 py-2 text-xs font-semibold transition-all ${
                showQuiz
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <svg className="mr-1 h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Final Knowledge Check
            </button>
          </div>

          {showQuiz ? (
            <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                    Module Evaluation
                  </span>
                  <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
                    Pass threshold: 70%
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Knowledge Check: {module.title || 'Untitled module'}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Answer all {module.quiz.length} questions based on the module content. Answers are
                  not graded in preview.
                </p>
              </div>

              {module.quiz.length === 0 ? (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Add at least one quiz question before this module can be published.
                </p>
              ) : (
                <div className="space-y-6">
                  {module.quiz.map((question, questionIndex) => {
                    const options = lines(question.optionsText);
                    return (
                      <div
                        key={`${question.question}-${questionIndex}`}
                        className="rounded-xl border border-gray-200 bg-white p-4"
                      >
                        <h4 className="mb-3 text-sm font-semibold text-gray-900">
                          {questionIndex + 1}. {question.question || 'Untitled question'}
                        </h4>
                        <div className="space-y-2">
                          {options.map((option, optionIndex) => (
                            <label
                              key={`${option}-${optionIndex}`}
                              className={`flex cursor-pointer items-start rounded-lg border p-3 text-xs transition-colors ${
                                selectedAnswers[questionIndex] === optionIndex
                                  ? 'border-purple-600 bg-purple-50 font-medium text-purple-950'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`preview-question-${questionIndex}`}
                                checked={selectedAnswers[questionIndex] === optionIndex}
                                onChange={() =>
                                  setSelectedAnswers((previous) => ({
                                    ...previous,
                                    [questionIndex]: optionIndex,
                                  }))
                                }
                                className="mr-2.5 mt-0.5 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="flex-1">{renderFormattedText(option)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuiz(false);
                    setActiveLessonIndex(Math.max(lessonCount - 1, 0));
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                >
                  ← Review Lessons
                </button>
                <button
                  type="button"
                  disabled
                  className="cursor-not-allowed rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-2.5 text-xs font-semibold text-white opacity-50 shadow-sm"
                  title="Submissions are disabled in preview"
                >
                  Submit Knowledge Check
                </button>
              </div>
            </div>
          ) : activeLesson ? (
            <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-600">
                    Lesson {activeLessonIndex + 1} of {lessonCount}
                  </span>
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                    ⏱ {activeLesson.duration || 'Self-paced'}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {activeLesson.title || 'Untitled lesson'}
                </h3>
              </div>

              {activeLesson.summary && (
                <div className="rounded-xl border-l-4 border-purple-600 bg-purple-50 p-4 text-sm italic text-purple-950">
                  "{activeLesson.summary}"
                </div>
              )}

              <div className="max-w-none text-sm leading-relaxed text-gray-700">
                {activeLesson.contentMarkdown.trim() ? (
                  <LessonContentRenderer markdown={activeLesson.contentMarkdown} />
                ) : (
                  <p className="text-sm text-gray-400">No lesson body has been written yet.</p>
                )}
              </div>

              <LessonMediaBlock media={activeLesson.media || []} />

              {lines(activeLesson.takeawaysText).length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <h4 className="mb-2 flex items-center text-xs font-bold uppercase tracking-wider text-emerald-900">
                    <svg className="mr-1.5 h-4 w-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Key Executive Takeaways
                  </h4>
                  <ul className="space-y-1.5 text-xs text-emerald-900">
                    {lines(activeLesson.takeawaysText).map((item, index) => (
                      <li key={`${item}-${index}`} className="flex items-start">
                        <span className="mr-2 font-bold text-emerald-500">•</span>
                        <span>{renderFormattedText(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  disabled={activeLessonIndex === 0}
                  onClick={() =>
                    setActiveLessonIndex((previous) => Math.max(0, previous - 1))
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                >
                  ← Previous Lesson
                </button>

                <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                  Preview only — nothing is saved or marked complete
                </span>

                {activeLessonIndex < lessonCount - 1 ? (
                  <button
                    type="button"
                    onClick={() => setActiveLessonIndex((previous) => previous + 1)}
                    className="rounded-lg bg-purple-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-purple-700"
                  >
                    Next Lesson →
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowQuiz(true)}
                    disabled={module.quiz.length === 0}
                    className="flex items-center space-x-1 rounded-lg bg-indigo-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span>Take Knowledge Check</span>
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-8 text-sm text-amber-900">
              Add a lesson to see how this module will appear to learners.
            </div>
          )}

          {!showQuiz && module.description && (
            <p className="px-1 text-xs text-slate-500">{module.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcademyModulePreview;
